import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FieldSelect } from "@/components/ui/field-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Loader2, FileText, ExternalLink, ArrowRightLeft, XCircle, RotateCcw } from "lucide-react";

const STATUS_STYLE = {
  Actif: "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Paiement en retard": "border-amber-200 bg-amber-50 text-amber-700",
  "Accès restreint": "border-rose-200 bg-rose-50 text-rose-700",
  "Résiliation programmée": "border-amber-200 bg-amber-50 text-amber-700",
  Résilié: "border-slate-300 bg-slate-100 text-slate-500",
  "Paiement en attente": "border-amber-200 bg-amber-50 text-amber-700",
};

function fmtDate(v) {
  if (v == null) return "—";
  const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}
function fmtMoney(minor, currency) {
  try {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: (currency || "eur").toUpperCase() }).format((minor || 0) / 100);
  } catch { return `${((minor || 0) / 100).toFixed(2)} ${currency || ""}`; }
}

export default function SubscriptionBilling({ orgId }) {
  const qc = useQueryClient();
  const { reload } = useAuth();
  const [changeOpen, setChangeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const { data: sub, isLoading, isError, refetch } = useQuery({
    queryKey: ["subscription", orgId],
    queryFn: async () => (await api.get("/api/billing/subscription", { params: { organization_id: orgId } })).data,
    enabled: !!orgId, retry: false,
  });
  const { data: invData, isLoading: invLoading } = useQuery({
    queryKey: ["invoices", orgId],
    queryFn: async () => (await api.get("/api/billing/invoices", { params: { organization_id: orgId } })).data,
    enabled: !!orgId, retry: false,
  });
  const invoices = invData?.invoices || [];

  const afterMutation = () => { qc.invalidateQueries({ queryKey: ["subscription", orgId] }); reload(); refetch(); };

  const portal = useMutation({
    mutationFn: async () => (await api.post("/api/billing/portal", {}, { params: { organization_id: orgId } })).data,
    onSuccess: (d) => { window.location.href = d.url; },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const cancel = useMutation({
    mutationFn: async () => (await api.post("/api/billing/cancel", {}, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Résiliation programmée en fin de période"); setCancelOpen(false); afterMutation(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const resume = useMutation({
    mutationFn: async () => (await api.post("/api/billing/resume", {}, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Résiliation annulée"); afterMutation(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  if (isLoading) return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="billing-loading">
      <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
    </div>
  );
  if (isError || !sub) return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" data-testid="billing-error">
      Impossible de charger votre abonnement. <button onClick={() => refetch()} className="font-semibold underline">Réessayer</button>
    </div>
  );

  const badge = STATUS_STYLE[sub.status_label] || "border-slate-200 bg-slate-50 text-slate-600";
  const hasSub = sub.has_stripe;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="subscription-block">
      <div className="flex items-center gap-2 border-b border-slate-100 p-4">
        <CreditCard className="h-4 w-4 text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-800">Abonnement &amp; facturation</h3>
      </div>

      <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <p className="text-xs font-medium text-slate-400">Offre</p>
          <p className="mt-1 text-lg font-bold text-slate-900" data-testid="sub-plan-name">{sub.plan_name || "—"}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-400">Prix mensuel</p>
          <p className="mt-1 text-lg font-bold text-slate-900" data-testid="sub-price">{sub.price_eur != null ? `${sub.price_eur} € / mois` : "—"}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-400">Statut</p>
          <span data-testid="sub-status" className={`mt-1 inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${badge}`}>{sub.status_label}</span>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-400">{sub.cancel_at_period_end ? "Fin d'accès le" : "Prochaine échéance"}</p>
          <p className="mt-1 text-sm font-semibold text-slate-800" data-testid="sub-next-date">{fmtDate(sub.current_period_end)}</p>
        </div>
      </div>

      {sub.cancel_at_period_end && (
        <div className="mx-4 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="sub-cancel-notice">
          Votre abonnement sera résilié le {fmtDate(sub.current_period_end)}. Vous conservez l'accès jusqu'à cette date.
        </div>
      )}
      {sub.scheduled_plan && (
        <div className="mx-4 mb-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="sub-scheduled-notice">
          Changement programmé vers l'offre {sub.scheduled_plan_name} le {fmtDate(sub.scheduled_plan_effective)}.
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-t border-slate-100 p-4">
        <button onClick={() => portal.mutate()} disabled={!hasSub || portal.isPending} data-testid="manage-billing-button"
          className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {portal.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Gérer mon abonnement
        </button>
        <button onClick={() => setChangeOpen(true)} disabled={!hasSub} data-testid="change-plan-button"
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
          <ArrowRightLeft className="h-4 w-4" /> Changer de formule
        </button>
        {sub.cancel_at_period_end ? (
          <button onClick={() => resume.mutate()} disabled={resume.isPending} data-testid="resume-sub-button"
            className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
            {resume.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Reprendre l'abonnement
          </button>
        ) : (
          <button onClick={() => setCancelOpen(true)} disabled={!hasSub} data-testid="cancel-sub-button"
            className="flex items-center gap-1.5 rounded-lg border border-rose-300 bg-white px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50">
            <XCircle className="h-4 w-4" /> Résilier
          </button>
        )}
      </div>

      {/* Invoices */}
      <div className="border-t border-slate-100 p-4" data-testid="invoices-section">
        <div className="mb-2 flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" /><h4 className="text-sm font-semibold text-slate-700">Factures</h4></div>
        {invLoading ? (
          <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" /></div>
        ) : invoices.length === 0 ? (
          <p className="py-4 text-sm text-slate-400" data-testid="invoices-empty">Aucune facture disponible pour le moment.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-400"><tr>{["Numéro", "Date", "Montant", "Statut", ""].map((h) => <th key={h} className="py-2 pr-4">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((i) => (
                  <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                    <td className="py-2 pr-4 font-medium text-slate-700">{i.number || i.id.slice(-8)}</td>
                    <td className="py-2 pr-4 text-slate-500">{fmtDate(i.created)}</td>
                    <td className="py-2 pr-4 text-slate-700">{fmtMoney(i.amount, i.currency)}</td>
                    <td className="py-2 pr-4">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${i.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{i.status === "paid" ? "Payée" : i.status}</span>
                    </td>
                    <td className="py-2 pr-0 text-right">
                      {(i.pdf || i.hosted) && (
                        <a href={i.pdf || i.hosted} target="_blank" rel="noopener noreferrer" data-testid={`invoice-pdf-${i.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                          <FileText className="h-3.5 w-3.5" /> PDF
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ChangePlanDialog open={changeOpen} onOpenChange={setChangeOpen} sub={sub} orgId={orgId} onDone={afterMutation} />

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-md" data-testid="cancel-dialog">
          <DialogHeader><DialogTitle>Résilier l'abonnement</DialogTitle>
            <DialogDescription>Votre abonnement restera actif jusqu'au {fmtDate(sub.current_period_end)}. Aucune donnée ne sera supprimée.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setCancelOpen(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Annuler</button>
            <button onClick={() => cancel.mutate()} disabled={cancel.isPending} data-testid="cancel-confirm" className="flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
              {cancel.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Confirmer la résiliation
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChangePlanDialog({ open, onOpenChange, sub, orgId, onDone }) {
  const options = (sub.available_plans || []).map((p) => ({ value: p.key, label: `${p.name} — ${p.price_eur} € / mois` }));
  const [target, setTarget] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post("/api/billing/change-plan", { plan: target }, { params: { organization_id: orgId } })).data,
    onSuccess: (d) => {
      toast.success(d.effect === "immediate" ? "Offre mise à niveau immédiatement" : "Changement programmé à la prochaine échéance");
      onOpenChange(false); setTarget(""); onDone();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const rank = { pro: 1, business: 2, premium: 3 };
  const isDowngrade = target && rank[target] < rank[sub.plan];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="change-plan-dialog">
        <DialogHeader><DialogTitle>Changer de formule</DialogTitle>
          <DialogDescription>Offre actuelle : {sub.plan_name}. Un upgrade est appliqué immédiatement (prorata Stripe) ; un downgrade prend effet à la prochaine échéance.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FieldSelect testId="change-plan-select" className="w-full" value={target} onChange={setTarget}
            options={options.filter((o) => o.value !== sub.plan)} placeholder="Choisir une offre" />
          {isDowngrade && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Le passage à une offre inférieure prendra effet le {fmtDate(sub.current_period_end)}. Aucune donnée ne sera supprimée ; seules les nouvelles créations au-delà des limites seront bloquées.
            </p>
          )}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Annuler</button>
          <button onClick={() => mut.mutate()} disabled={!target || mut.isPending} data-testid="change-plan-confirm" className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Confirmer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
